/* eslint-disable newline-before-return */
import { CONFIG } from "@/components/FormEngine/config";
import { FieldError, RawField } from "@/components/FormEngine/types";
import {
  isNonEmptyString,
  isObject,
  isValidBoolean,
  isValidNumber,
} from "@/components/FormEngine/utils";

/* ──────────────────────────────────────────────────────────────
   Standard error messages
   ────────────────────────────────────────────────────────────── */
const ERROR_MESSAGES = {
  undefined: "Undefined",
  null: "Null",
  invalidString: "Empty string",
  invalidBoolean: "Invalid boolean",
  invalidNumber: "Invalid number",
} as const;

/* ──────────────────────────────────────────────────────────────
   Strict definition of required RAW fields
   ────────────────────────────────────────────────────────────── */
type ExpectedPrimitiveType = "string" | "boolean" | "number";

type RawFieldRequirement = Readonly<{
  key: keyof RawField & string;
  type: ExpectedPrimitiveType;
}>;

const RAW_FIELD_REQUIREMENTS: readonly RawFieldRequirement[] = [
  { key: "name", type: "string" },
  { key: "type", type: "string" },
  { key: "required", type: "boolean" },
  { key: "writable", type: "boolean" },
  // { key: "step", type: "number" },
  // { key: "row", type: "number" },
  // { key: "col", type: "number" },
] as const;

/* ──────────────────────────────────────────────────────────────
   Basic validations by expected type
   ────────────────────────────────────────────────────────────── */
const validatePrimitiveType = (
  value: unknown,
  expectedType: ExpectedPrimitiveType,
): string | null => {
  if (value === undefined) return ERROR_MESSAGES.undefined;
  if (value === null) return ERROR_MESSAGES.null;

  switch (expectedType) {
    case "string":
      return isNonEmptyString(value) ? null : ERROR_MESSAGES.invalidString;
    case "boolean":
      return isValidBoolean(value) ? null : ERROR_MESSAGES.invalidBoolean;
    case "number":
      return isValidNumber(value) ? null : ERROR_MESSAGES.invalidNumber;
  }
};

/* ──────────────────────────────────────────────────────────────
   Validation of required RAW fields
   ────────────────────────────────────────────────────────────── */
const validateRawFieldRequirements = (raw: RawField): FieldError[] => {
  const errors: FieldError[] = [];
  for (const requirement of RAW_FIELD_REQUIREMENTS) {
    const value = (raw as Record<string, unknown>)[requirement.key];
    const errorMessage = validatePrimitiveType(value, requirement.type);
    if (errorMessage) {
      errors.push({ key: requirement.key, message: errorMessage });
    }
  }
  return errors;
};

/* ──────────────────────────────────────────────────────────────
   Specific validation for fields with options
   ────────────────────────────────────────────────────────────── */
const validateFieldOptionsIfRequired = (raw: RawField): FieldError[] => {
  const typeName = String(raw.type ?? "");
  const requiresOptions = CONFIG.FIELD_TYPES_WITH_OPTIONS.includes(
    typeName as (typeof CONFIG.FIELD_TYPES_WITH_OPTIONS)[number],
  );
  if (!requiresOptions) return [];

  const errors: FieldError[] = [];
  const options = raw.options;

  if (!Array.isArray(options)) {
    errors.push({ key: "options", message: ERROR_MESSAGES.undefined });
    return errors;
  }

  for (const option of options) {
    if (!isObject(option)) {
      errors.push({ key: "options", message: ERROR_MESSAGES.undefined });
      break;
    }
    const hasValidIdentifier =
      isNonEmptyString((option as any).id) ||
      isNonEmptyString((option as any).value) ||
      isNonEmptyString((option as any).text);
    if (!hasValidIdentifier) {
      errors.push({ key: "options", message: ERROR_MESSAGES.undefined });
      break;
    }
  }

  return errors;
};

/* ──────────────────────────────────────────────────────────────
   Main function: returns all errors of a RawField
   ────────────────────────────────────────────────────────────── */
export const getRawFieldErrors = (raw: RawField): FieldError[] => {
  if (!isObject(raw)) {
    return RAW_FIELD_REQUIREMENTS.map(({ key }) => ({
      key,
      message: ERROR_MESSAGES.undefined,
    }));
  }

  return [
    ...validateRawFieldRequirements(raw),
    ...validateFieldOptionsIfRequired(raw),
  ];
};
